// EventPanel.js — v7 : images fixées, résumés complets, lien Wikipedia

import React, { useEffect, useState, useRef } from 'react';
import { CATEGORIES, formatYear } from '../data/events';
import { getEnrichedEvent, getEnrichedEventAsync } from '../lib/enrichedEvents';

// ── Wikipedia : image + URL de la page ───────────────────────────────────────
async function fetchWikipediaData(title) {
  // Génère plusieurs variantes du titre pour maximiser les chances
  const clean = (s) => s
    .replace(/['']/g, "'")
    .replace(/\s*—.*$/, '')
    .replace(/\s*-\s.*$/, '')
    .replace(/^(La |Le |Les |L'|L')/, '')
    .replace(/\s+/g, ' ')
    .trim();

  const variants = [
    title,
    clean(title),
    title.replace(/\s*—.*$/, '').trim(),
    title.split(' ').slice(0, 3).join(' '),
    title.split(' ').slice(0, 2).join(' '),
  ].filter((v, i, a) => v && v.length > 2 && a.indexOf(v) === i);

  for (const variant of variants) {
    for (const lang of ['fr', 'en']) {
      try {
        // Recherche directe par titre
        const url = `https://${lang}.wikipedia.org/w/api.php?` +
          `action=query&titles=${encodeURIComponent(variant)}` +
          `&prop=pageimages|info&inprop=url` +
          `&format=json&pithumbsize=800&origin=*`;
        const res = await fetch(url, { signal: AbortSignal.timeout(5000) });
        if (!res.ok) continue;
        const data  = await res.json();
        const pages = Object.values(data.query?.pages || {});
        const page  = pages[0];
        if (!page || page.missing !== undefined) continue;
        const imageUrl = page.thumbnail?.source || null;
        const wikiUrl  = page.fullurl || null;
        if (imageUrl || wikiUrl) return { imageUrl, wikiUrl };
      } catch { continue; }
    }
  }

  // Dernier recours : recherche full-text Wikipedia
  try {
    for (const lang of ['fr', 'en']) {
      const searchUrl = `https://${lang}.wikipedia.org/w/api.php?` +
        `action=query&list=search&srsearch=${encodeURIComponent(clean(title))}` +
        `&srlimit=1&format=json&origin=*`;
      const res = await fetch(searchUrl, { signal: AbortSignal.timeout(4000) });
      if (!res.ok) continue;
      const data = await res.json();
      const hit  = data.query?.search?.[0];
      if (!hit) continue;

      const url2 = `https://${lang}.wikipedia.org/w/api.php?` +
        `action=query&pageids=${hit.pageid}` +
        `&prop=pageimages|info&inprop=url` +
        `&format=json&pithumbsize=800&origin=*`;
      const res2 = await fetch(url2, { signal: AbortSignal.timeout(4000) });
      if (!res2.ok) continue;
      const data2 = await res2.json();
      const pages = Object.values(data2.query?.pages || {});
      const page  = pages[0];
      if (!page) continue;
      return {
        imageUrl: page.thumbnail?.source || null,
        wikiUrl:  page.fullurl || null,
      };
    }
  } catch { /* abandon */ }

  return { imageUrl: null, wikiUrl: null };
}

// ── Enrichissement Claude temps réel (fallback) ───────────────────────────────
async function fetchLiveEnrichment(event) {
  try {
    const yearLabel = event.year < 0
      ? `${Math.abs(event.year)} av. J.-C.`
      : `${event.year} ap. J.-C.`;

    const res = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 1200,
        messages: [{
          role: 'user',
          content: `Tu es un historien expert et rédacteur encyclopédique de talent.
Pour l'événement historique suivant, génère une fiche complète en JSON.

Événement : "${event.title}"
Année : ${yearLabel}
Catégorie : ${event.category}
Description de base : ${event.description}

Réponds UNIQUEMENT avec ce JSON (sans markdown, sans texte autour) :
{
  "resume": "Résumé complet et vivant en 5-6 phrases. Style encyclopédie de qualité. Donne les faits précis, les dates, les noms. Rends l'événement passionnant.",
  "contexte": "Contexte historique détaillé en 3-4 phrases. Explique la situation politique, sociale et économique qui a mené à cet événement. Cite les causes profondes.",
  "consequences": "Conséquences détaillées en 3-4 phrases. Impact immédiat puis à long terme sur l'Histoire mondiale. Chiffres et faits précis si possible.",
  "anecdote": "Une anecdote vraie, surprenante et peu connue, directement liée à cet événement. Minimum 2 phrases.",
  "personnages": ["Nom complet 1", "Nom complet 2", "Nom complet 3"],
  "mots_cles": ["mot1", "mot2", "mot3", "mot4", "mot5"]
}`
        }],
      }),
    });

    if (!res.ok) return null;
    const data = await res.json();
    const text = data.content?.map(b => b.text || '').join('') || '';
    const clean = text.replace(/```json|```/g, '').trim();
    return JSON.parse(clean);
  } catch { return null; }
}

// ── Composant ─────────────────────────────────────────────────────────────────
export function EventPanel({ event, allEvents, onClose, onNavigateTo, user, onEdit, onReport, hideHeader = false }) {
  const [visible, setVisible] = useState(false);
  const [imgUrl, setImgUrl]   = useState(null);
  const [wikiUrl, setWikiUrl] = useState(null);
  const [imgLoad, setImgLoad] = useState(false);
  const [aiData, setAiData]   = useState(null);
  const [aiLoad, setAiLoad]   = useState(false);
  const abortRef              = useRef(null);

  useEffect(() => {
    if (event) setTimeout(() => setVisible(true), 10);
    else { setVisible(false); setImgUrl(null); setWikiUrl(null); setAiData(null); }
  }, [event]);

  useEffect(() => {
    if (!event) return;

    // Reset
    setImgUrl(null);
    setWikiUrl(null);
    setAiData(null);
    setImgLoad(true);
    setAiLoad(true);

    if (abortRef.current) abortRef.current = false;
    const alive = { current: true };
    abortRef.current = alive;

    (async () => {
      // 1. Image directe sur l'event (communauté)
      if (event.image_url) {
        if (alive.current) { setImgUrl(event.image_url); setImgLoad(false); }
      }

      // 2. Cherche données pré-enrichies Supabase
      const enriched = await getEnrichedEventAsync(event.id);

      if (!alive.current) return;

      if (enriched) {
        setAiData(enriched);
        if (enriched.image_url && !event.image_url) {
          setImgUrl(enriched.image_url);
          setImgLoad(false);
        }
        if (enriched.wiki_url) setWikiUrl(enriched.wiki_url);
      }
      setAiLoad(false);

      // 3. Wikipedia : image + URL (même si Supabase a les données texte)
      if (!event.image_url) {
        try {
          const { imageUrl, wikiUrl: wUrl } = await fetchWikipediaData(event.title);
          if (!alive.current) return;
          if (imageUrl) setImgUrl(imageUrl);
          if (wUrl) setWikiUrl(wUrl);
        } catch { /* silencieux */ }
        finally { if (alive.current) setImgLoad(false); }
      } else {
        setImgLoad(false);
      }

      setAiLoad(false);
    })();

    return () => { alive.current = false; };
  }, [event?.id]);

  if (!event) return null;

  const category  = CATEGORIES[event.category];
  const connected = (event.connections || [])
    .map(id => allEvents.find(e => e.id === id))
    .filter(Boolean);

  return (
    <div className="event-panel"
      style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(30px)' }}>

      {/* ── Photo ── */}
      <div className={`panel-img-wrap${hideHeader ? '' : ' drag-handle'}`} title={hideHeader ? '' : 'Glisser pour déplacer'}>
        {imgLoad && <div className="panel-img-skeleton" />}
        {imgUrl && (
          <img src={imgUrl} alt={event.title} className="panel-img"
            onLoad={() => setImgLoad(false)}
            onError={() => { setImgUrl(null); setImgLoad(false); }} />
        )}
        {!imgUrl && !imgLoad && (
          <div className="panel-img-placeholder" style={{ background: `${category.color}22` }}>
            <span style={{ fontSize: 40 }}>{category.icon}</span>
          </div>
        )}
        <div className="panel-img-overlay" />

        {/* Année en grand sur l'image */}
        <div className="panel-img-year">
          <span className="panel-img-year-num" style={{ color: category.color }}>
            {event.year < 0
              ? `${Math.abs(event.year)} av. J.-C.`
              : event.year}
          </span>
          <span className="panel-img-year-title">{event.title}</span>
        </div>

        {/* Lien Wikipedia sur la photo */}
        {wikiUrl && (
          <a href={wikiUrl} target="_blank" rel="noreferrer" className="wiki-link-photo">
            W Wikipédia →
          </a>
        )}
      </div>

      {/* ── Header avec poignée drag ── */}
      <div className="panel-header drag-handle" style={{ cursor: 'grab' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="drag-grip">⠿</span>
          <div className="panel-category" style={{ color: category.color }}>
            <span className="cat-icon">{category.icon}</span>
            <span className="cat-label">{category.label}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {user?.email === 'charl.delattre@gmail.com' && (
            <button className="edit-btn" onMouseDown={e => e.stopPropagation()} onClick={() => onEdit && onEdit(event)} title="Modifier la fiche">
              ✏️
            </button>
          )}
          <button className="close-btn" onMouseDown={e => e.stopPropagation()} onClick={onClose}>×</button>
        </div>
      </div>

      <div className="panel-year" style={{ borderColor: category.color }}>
        {formatYear(event.year)}
      </div>
      <h2 className="panel-title">{event.title}</h2>

      {/* ── Contenu enrichi ── */}
      {aiLoad ? (
        <div className="ai-loading">
          <div className="ai-spinner">
            {[0,1,2,3].map(i => <div key={i} className="spinner-bar" />)}
          </div>
          <span className="ai-loading-text">Rédaction de la fiche…</span>
        </div>
      ) : aiData ? (
        <div className="enriched-content">
          <p className="panel-desc">{aiData.resume || event.description}</p>

          {aiData.contexte && (
            <div className="enrich-section">
              <div className="enrich-label">🔍 Contexte historique</div>
              <p className="enrich-text">{aiData.contexte}</p>
            </div>
          )}

          {aiData.consequences && (
            <div className="enrich-section">
              <div className="enrich-label">⚡ Conséquences</div>
              <p className="enrich-text">{aiData.consequences}</p>
            </div>
          )}

          {aiData.anecdote && (
            <div className="enrich-anecdote">
              <span>💡</span>
              <p className="enrich-text">{aiData.anecdote}</p>
            </div>
          )}

          {aiData.personnages?.length > 0 && (
            <div className="enrich-tags">
              <div className="enrich-label">👤 Personnages clés</div>
              <div className="enrich-pills">
                {aiData.personnages.map((p, i) => (
                  <span key={i} className="enrich-pill" style={{ borderColor: category.color }}>{p}</span>
                ))}
              </div>
            </div>
          )}

          {aiData.mots_cles?.length > 0 && (
            <div className="enrich-pills" style={{ marginTop: 6 }}>
              {aiData.mots_cles.map((m, i) => (
                <span key={i} className="enrich-pill muted">{m}</span>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="panel-desc">{event.description}</p>
      )}

      {/* ── Meta ── */}
      <div className="panel-importance">
        <span className="imp-label">Importance :</span>
        {[1,2,3].map(i => (
          <span key={i} className="imp-dot"
            style={{ background: i <= event.importance ? category.color : 'rgba(255,255,255,0.15)' }} />
        ))}
      </div>

      <div className="panel-coords">
        📍 {event.lat.toFixed(2)}°, {event.lng.toFixed(2)}°
        {event.source === 'community' && (
          <span className="community-badge">👥 Communauté</span>
        )}
      </div>

      {/* ── Lien Wikipedia ── */}
      {wikiUrl && (
        <a href={wikiUrl} target="_blank" rel="noreferrer" className="wiki-link-btn">
          <span className="wiki-w">W</span>
          Lire sur Wikipédia
          <span className="wiki-arrow">↗</span>
        </a>
      )}

      {/* ── Vidéo YouTube ── */}
      {aiData?.video_embed_url && (
        <div className="yt-section">
          <div className="enrich-label">🎬 Vidéo</div>
          <div className="yt-player-wrap">
            <iframe
              src={`${aiData.video_embed_url}?rel=0&modestbranding=1`}
              title={aiData.video_title || event.title}
              className="yt-player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
              allowFullScreen
            />
          </div>
          {aiData.video_channel && (
            <div className="yt-credit">📺 {aiData.video_channel}</div>
          )}
        </div>
      )}

      {/* ── Événements liés ── */}
      {connected.length > 0 && (
        <div className="panel-connections">
          <div className="conn-title">Événements liés</div>
          <div className="conn-list">
            {connected.map(conn => {
              const c = CATEGORIES[conn.category];
              return (
                <button key={conn.id} className="conn-item" onClick={() => onNavigateTo(conn)}>
                  <span className="conn-icon">{c.icon}</span>
                  <span className="conn-name">{conn.title}</span>
                  <span className="conn-year" style={{ color: c.color }}>{formatYear(conn.year)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <button className="report-btn" onClick={() => onReport && onReport(event)}>
        🚩 Signaler une erreur
      </button>

      <button className="zoom-btn" onClick={() => onNavigateTo(event)}>
        <span>🔍</span> Centrer sur l'événement
      </button>
    </div>
  );
}
